import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import { 
  LogOut, Home, Search, Plus, Gift, Users, 
  Sun, Moon, ChevronDown, X, Check 
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_obsidian-tier/artifacts/za07r3ra_dungeon-eyes-wide-shut-logo-bigger-text.png";

export default function AdminPage() {
  const { member, logout } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddVisitModal, setShowAddVisitModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const res = await api.get("/admin/members");
      setMembers(res.data);
    } catch (error) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const searchMembers = async () => {
    if (!searchQuery.trim()) {
      loadMembers();
      return;
    }
    try {
      const res = await api.get(`/admin/search-members?q=${encodeURIComponent(searchQuery)}`);
      setMembers(res.data);
    } catch (error) {
      toast.error("Search failed");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleAddVisit = async (memberId, sessionType) => {
    try {
      const res = await api.post("/admin/add-visit", {
        member_id: memberId,
        session_type: sessionType,
      });
      toast.success(`Visit recorded for ${res.data.display_name}`);
      
      // Check if they earned a reward
      if (res.data.current_cycle_visits === 0) {
        toast.success("🎉 Free evening earned!", { duration: 5000 });
      }
      
      loadMembers();
      setShowAddVisitModal(false);
      setSelectedMember(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add visit");
    }
  };

  const handleRedeemEvening = async (memberId) => {
    try {
      const res = await api.post("/admin/redeem-evening", {
        member_id: memberId,
      });
      toast.success(`Free evening redeemed for ${res.data.display_name}`);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem");
    }
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'shadow_initiate': return 'tier-shadow-initiate';
      case 'crimson_devotee': return 'tier-crimson-devotee';
      case 'obsidian_master': return 'tier-obsidian-master';
      default: return 'tier-shadow-initiate';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-[#c5a059]/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src={LOGO_URL} 
              alt="Logo" 
              className="h-10 w-auto"
              data-testid="admin-logo"
            />
            <span className="font-cinzel text-sm tracking-[0.15em] uppercase text-[#8a0303]">
              Admin Portal
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 px-4 py-2 text-[#c5a059] hover:text-[#e5c07b] 
                border border-[#c5a059]/30 rounded hover:border-[#c5a059]/60 transition-all text-sm"
              data-testid="dashboard-link"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-[#8a0303] hover:text-[#b30000] 
                border border-[#8a0303]/30 rounded hover:border-[#8a0303]/60 transition-all text-sm"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="font-cinzel text-2xl tracking-[0.15em] uppercase mb-8 text-center" style={{ color: '#c5a059' }}>
          Member Management
        </h1>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="flex gap-2 max-w-xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c5a059]/40" />
              <input
                type="text"
                placeholder="Search by name, email, or member ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchMembers()}
                className="w-full bg-[#0a0a0a] border border-[#c5a059]/20 text-[#e5c07b] placeholder:text-[#c5a059]/30 
                  focus:border-[#c5a059] focus:ring-1 focus:ring-[#c5a059] rounded h-12 pl-12 pr-4 font-manrope"
                data-testid="search-input"
              />
            </div>
            <button
              onClick={searchMembers}
              className="px-6 bg-[#8a0303] hover:bg-[#b30000] text-white font-cinzel tracking-wider uppercase 
                rounded transition-all duration-300"
              data-testid="search-button"
            >
              Search
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card">
            <div className="stat-value">{members.length}</div>
            <div className="stat-label">Total Members</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {members.filter(m => m.tier === 'obsidian_master').length}
            </div>
            <div className="stat-label">Obsidian Masters</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {members.reduce((sum, m) => sum + m.total_visits, 0)}
            </div>
            <div className="stat-label">Total Visits</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {members.reduce((sum, m) => sum + (m.free_evenings_earned - m.free_evenings_redeemed), 0)}
            </div>
            <div className="stat-label">Pending Rewards</div>
          </div>
        </div>

        {/* Members Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-[#c5a059] animate-pulse font-cinzel">Loading shadows...</div>
          </div>
        ) : (
          <div className="admin-table rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left">Member</th>
                    <th className="text-left">ID</th>
                    <th className="text-center">Tier</th>
                    <th className="text-center">Visits</th>
                    <th className="text-center">Progress</th>
                    <th className="text-center">Rewards</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.filter(m => !m.is_admin).map((m) => {
                    const freeAvailable = m.free_evenings_earned - m.free_evenings_redeemed;
                    return (
                      <tr key={m.id} data-testid={`member-row-${m.id}`}>
                        <td>
                          <div>
                            <div className="font-semibold text-[#e5c07b]">{m.display_name}</div>
                            <div className="text-xs text-[#c5a059]/50">{m.email}</div>
                          </div>
                        </td>
                        <td className="font-mono text-sm text-[#c5a059]/70">{m.member_id}</td>
                        <td className="text-center">
                          <span className={`tier-badge ${getTierBadgeClass(m.tier)}`}>
                            {m.tier_name}
                          </span>
                        </td>
                        <td className="text-center font-semibold">{m.total_visits}</td>
                        <td className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <div 
                                key={i} 
                                className={`w-2 h-2 rounded-full ${
                                  i < m.current_cycle_visits 
                                    ? 'bg-[#c5a059]' 
                                    : 'bg-[#c5a059]/20'
                                }`}
                              />
                            ))}
                          </div>
                          <div className="text-xs text-[#c5a059]/50 mt-1">
                            {m.current_cycle_visits}/5
                          </div>
                        </td>
                        <td className="text-center">
                          {freeAvailable > 0 ? (
                            <span className="text-[#b30000] font-semibold">{freeAvailable} available</span>
                          ) : (
                            <span className="text-[#c5a059]/30">None</span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedMember(m);
                                setShowAddVisitModal(true);
                              }}
                              className="p-2 bg-[#8a0303]/20 hover:bg-[#8a0303]/40 text-[#ff6b6b] 
                                rounded transition-colors" 
                              title="Add Visit"
                              data-testid={`add-visit-${m.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            {freeAvailable > 0 && (
                              <button
                                onClick={() => handleRedeemEvening(m.id)}
                                className="p-2 bg-[#c5a059]/20 hover:bg-[#c5a059]/40 text-[#e5c07b] 
                                  rounded transition-colors"
                                title="Redeem Free Evening"
                                data-testid={`redeem-${m.id}`}
                              >
                                <Gift className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Add Visit Modal */}
      {showAddVisitModal && selectedMember && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowAddVisitModal(false)}
        >
          <div 
            className="glass-obsidian rounded-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="add-visit-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-cinzel text-lg tracking-wider" style={{ color: '#c5a059' }}>
                Record Visit
              </h2>
              <button
                onClick={() => setShowAddVisitModal(false)}
                className="text-[#c5a059]/50 hover:text-[#c5a059] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-[#e5c07b] font-semibold">{selectedMember.display_name}</p>
              <p className="text-[#c5a059]/50 text-sm">{selectedMember.member_id}</p>
              <p className="text-[#c5a059]/50 text-sm mt-2">
                Current progress: {selectedMember.current_cycle_visits}/5
              </p>
            </div>

            <p className="text-[#c5a059]/70 text-sm mb-4">Select session type:</p>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleAddVisit(selectedMember.id, 'afternoon')}
                className="flex flex-col items-center gap-2 p-4 bg-[#0a0a0a] border border-[#c5a059]/20 
                  rounded-lg hover:border-[#c5a059]/60 transition-all"
                data-testid="session-afternoon"
              >
                <Sun className="w-8 h-8 text-[#c5a059]" />
                <span className="text-[#e5c07b] font-cinzel tracking-wider">Afternoon</span>
              </button>
              <button
                onClick={() => handleAddVisit(selectedMember.id, 'evening')}
                className="flex flex-col items-center gap-2 p-4 bg-[#0a0a0a] border border-[#8a0303]/30 
                  rounded-lg hover:border-[#8a0303]/60 transition-all"
                data-testid="session-evening"
              >
                <Moon className="w-8 h-8 text-[#8a0303]" />
                <span className="text-[#ff6b6b] font-cinzel tracking-wider">Evening</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
